import type { SignUpMethod } from '@/lib/analytics/events'

/**
 * The one-shot flag that turns a server-side workspace creation into exactly
 * one client-side GA4 `sign_up`.
 *
 * WHERE it is set matters more than anything else in this file. Sign-up mints a
 * User plus a placeholder "Pending Setup" / `temp-*` merchant in
 * /api/auth/sign-up-magic BEFORE the magic link is even sent — that row exists
 * for every abandoned or bounced attempt, so it is not a sign-up. The flag is
 * set only by /api/auth/complete-profile, after the email is verified, when the
 * placeholder is promoted to (or replaced by) a real, named workspace.
 *
 * WHY a cookie: the creation happens in a route handler, and GA's tag lives in
 * the browser. The cookie is deliberately NOT httpOnly so the page can read it,
 * and it is deleted before the event is sent, so a reload, a back button or a
 * second tab cannot count the same workspace twice. It carries no PII — the
 * method, an optional plan, and the opaque User.id for GA's `user_id`.
 *
 * Pure functions only (no `document`, no `window`) so the parse/consume logic is
 * unit-testable under vitest's node environment; the browser wiring passes the
 * cookie string and a deleter in.
 */

export const SIGNUP_FLAG_COOKIE = 'st_signup_event'

/** Long enough to survive a slow redirect into Stripe and back; short enough that a stale flag can't fire days later. */
export const SIGNUP_FLAG_MAX_AGE_SECONDS = 60 * 60

export type SignUpFlag = {
  method: SignUpMethod
  /** Lower-case plan key ("starter", "pro", "enterprise") when the server knows it. */
  plan?: string
  /** Internal User.id — becomes GA's user_id. Never an email. */
  uid?: string
}

const METHODS: readonly SignUpMethod[] = ['email', 'microsoft', 'apple']
const PLAN_RE = /^[a-z][a-z0-9_-]{0,31}$/
const UID_RE = /^[A-Za-z0-9_-]{1,64}$/

/**
 * How the account was created, from the user's better-auth Account rows.
 *
 * The EARLIEST account decides: someone who signed up by magic link and later
 * linked Microsoft in Security settings still signed up by email. Magic-link
 * users may have no Account row at all, or a `credential` one once they set a
 * password — both read as "email".
 */
export function signUpMethodFromAccounts(
  accounts: Array<{ providerId: string; createdAt: Date | string }>,
): SignUpMethod {
  const first = [...accounts].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  )[0]
  if (first?.providerId === 'microsoft' || first?.providerId === 'apple') return first.providerId
  return 'email'
}

/** Normalise a plan key from storage or the server ("PRO" → "pro"); null when absent or junk. */
export function normalizePlan(plan: string | null | undefined): string | undefined {
  const p = (plan ?? '').trim().toLowerCase()
  return PLAN_RE.test(p) ? p : undefined
}

/**
 * The cookie value as plain JSON. NOT pre-encoded: Next's `res.cookies.set`
 * percent-encodes the value itself, and encoding here too yields `%257B…`,
 * which the page decodes once, fails to parse and silently drops.
 */
export function serializeSignUpFlag(flag: SignUpFlag): string {
  const out: SignUpFlag = { method: flag.method }
  const plan = normalizePlan(flag.plan)
  if (plan) out.plan = plan
  if (flag.uid && UID_RE.test(flag.uid)) out.uid = flag.uid
  return JSON.stringify(out)
}

/** Parse a raw (percent-encoded, as `document.cookie` shows it) cookie value. Anything malformed is null — a forged or truncated flag must never fire. */
export function parseSignUpFlag(raw: string | null | undefined): SignUpFlag | null {
  if (!raw) return null
  let data: unknown
  try {
    data = JSON.parse(decodeURIComponent(raw))
  } catch {
    return null
  }
  if (!data || typeof data !== 'object') return null
  const { method, plan, uid } = data as Record<string, unknown>
  if (typeof method !== 'string' || !(METHODS as readonly string[]).includes(method)) return null
  const flag: SignUpFlag = { method: method as SignUpMethod }
  if (typeof plan === 'string' && normalizePlan(plan)) flag.plan = normalizePlan(plan)
  if (typeof uid === 'string' && UID_RE.test(uid)) flag.uid = uid
  return flag
}

/** Pull our cookie's value out of a `document.cookie` string. */
export function readCookieValue(cookieString: string, name = SIGNUP_FLAG_COOKIE): string | null {
  for (const part of cookieString.split(';')) {
    const eq = part.indexOf('=')
    if (eq < 0) continue
    if (part.slice(0, eq).trim() === name) return part.slice(eq + 1).trim()
  }
  return null
}

/**
 * Read-and-delete. Deletes BEFORE returning so the caller cannot fire twice
 * even if sending throws, and deletes a malformed flag too rather than leaving
 * it to be re-parsed on every page. `fallbackPlan` is the plan clicked on
 * /pricing (localStorage `st.selectedPlan`), used when the server didn't know.
 */
export function consumeSignUpFlag(opts: {
  cookieString: string
  deleteCookie: () => void
  fallbackPlan?: string | null
}): SignUpFlag | null {
  const raw = readCookieValue(opts.cookieString)
  if (raw === null) return null
  opts.deleteCookie()
  const flag = parseSignUpFlag(raw)
  if (!flag) return null
  if (!flag.plan) {
    const plan = normalizePlan(opts.fallbackPlan)
    if (plan) flag.plan = plan
  }
  return flag
}
